import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Icon } from 'components/common/icons';

import { useBoxContext } from './BoxContext';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  cellBreakpoint?: number;
  children?: React.ReactNode;
  emptyValue?: string;
  icon?: IconProp;
  iconStyle?: React.CSSProperties;
  label?: string;
  shrink?: boolean;
  sticky?: boolean;
  stickyAnchor?: 'left' | 'right';
  stickyBackgroundColor?: string;
  stickyOffset?: number;
  style?: React.CSSProperties;
  tooltip?: string;
  valueContainerStyle?: React.CSSProperties;
  verticalAlign?: 'bottom' | 'middle' | 'top';
  verticalBreakpoint?: number;
  verticalText?: boolean;
  verticalTextMaxHeight?: number;
}

const BoxTableCell = ({
  cellBreakpoint,
  children,
  emptyValue = '',
  icon,
  iconStyle,
  label,
  shrink,
  sticky = false,
  stickyAnchor = 'left',
  stickyBackgroundColor,
  stickyOffset = 0,
  style,
  tooltip,
  valueContainerStyle,
  verticalAlign = 'middle',
  verticalBreakpoint,
  verticalText = false,
  verticalTextMaxHeight = 120
}: Props) => {
  const { checkboxIsActive } = useBoxContext();

  const { theme } = useReduxState();

  const leftOffsetForSticky = stickyOffset + (checkboxIsActive ? 48 : 0);
  const rightOffsetForSticky = stickyOffset;

  return (
    <div
      className={
        `box-table-cell` +
        `${cellBreakpoint ? ' break' : ''}` +
        `${sticky ? ' sticky' : ''}` +
        `${sticky && stickyAnchor === 'left' ? ' sticky-left' : ''}` +
        `${sticky && stickyAnchor === 'right' ? ' sticky-right' : ''}` +
        `${verticalBreakpoint ? ' vertical-breakpoint' : ''}`
      }
      style={style}
    >
      {cellBreakpoint && label ? <div className="label">{label}</div> : null}

      <div
        className={
          `value-container` +
          `${verticalText ? ' value-container-vertical' : ''}`
        }
        data-tooltip={tooltip ? tooltip : undefined}
        style={valueContainerStyle}
      >
        {children && icon ? (
          <div className="icon" style={iconStyle}>
            <Icon icon={icon} />
          </div>
        ) : null}
        <div className="value">{children ? children : emptyValue}</div>
      </div>

      <style jsx>{`
        .box-table-cell {
          display: table-cell;
          padding: 10px 15px;
          vertical-align: ${verticalAlign};
          width: ${shrink ? '1%' : 'auto'};
        }
        .label {
          display: none;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .value-container {
          align-items: baseline;
          display: flex;
        }
        .value-container-vertical {
          display: block;
          max-height: ${verticalTextMaxHeight}px;
          overflow: hidden;
          text-overflow: ellipsis;
          transform: rotate(180deg);
          white-space: nowrap;
          writing-mode: vertical-lr;
          -webkit-writing-mode: vertical-lr;
        }
        .icon {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          margin-right: 5px;
        }
        @media (max-width: ${cellBreakpoint - 1}px) {
          .box-table-cell {
            width: auto;
          }
          .break {
            display: block;
          }
          .label {
            color: ${theme.textColorLighter};
            display: inline-block;
            font-size: 1.3rem;
            font-weight: 700;
            margin-right: 10px;
            width: 120px;
          }
          .label + .value {
            display: inline-block;
            vertical-align: top;
          }
        }
        @media (max-width: ${verticalBreakpoint - 1}px) {
          .vertical-breakpoint {
            vertical-align: top;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .sticky {
            background-color: ${stickyBackgroundColor || 'inherit'};
            opacity: 1;
            position: sticky;
            z-index: 2;
          }
          .sticky-left {
            left: ${leftOffsetForSticky}px;
          }
          .sticky-right {
            right: ${rightOffsetForSticky}px;
          }
        }
      `}</style>
    </div>
  );
};

export default BoxTableCell;
